import { createContext, useContext } from "react";

export const DEFAULT_CONTENT = {
  hero: {
    overline: "Intimacy Coordinator — Film · Television · Theatre",
    line1: "Chloe",
    line2: "Donan",
    tagline: "Choreographing intimacy with the same rigour as any stunt or dance — so every artist can do brave work, safely.",
    cta: "Work together",
    caption: "On set — where care meets craft",
  },
  marquee: { words: ["Consent", "Choreography", "Communication", "Care", "Craft", "Trust"] },
  colors: { ribbonStar: "#D8C5AF", bullet: "#5A2633" },
  header: {
    logoLeft: "C",
    logoRight: "D",
    tabs: { about: "About", resume: "Résumé", training: "Training", services: "Services", testimonials: "Testimonials", contact: "Contact", resources: "Resources" },
  },
  resumePhotos: [],
  headshot: "",
  resources: {
    title: "Resources",
    intro: "Reading, watching, and reference material I recommend to productions, performers, and students.",
    links: [],
    graphics: [],
  },
  chapters: {
    about: { kicker: "The Story", title: "About Chloe" },
    resume: { kicker: "Selected Credits", title: "Résumé" },
    training: { kicker: "Credentials", title: "Training & Certification" },
    services: { kicker: "What I Offer", title: "Services" },
    testimonials: { kicker: "Kind Words", title: "Testimonials" },
    contact: { kicker: "Get in Touch", title: "Contact" },
  },
  services: [
    { title: "Intimacy Coordination", context: "Film · TV · Verticals", detail: "On-set choreography of intimate scenes, consent conversations, and liaison between actors, directors, and production." },
    { title: "Intimacy Direction", context: "Theatre", detail: "Staging intimacy for the rehearsal room and the stage — repeatable, story-driven, and safe for eight shows a week." },
    { title: "Mental Health First Aid Support", context: "Set & Stage", detail: "Certified mental health first aid for your production — a calm, trained point of support for cast and crew." },
    { title: "Script Intimacy Consultation", context: "Pre-production", detail: "Script review, intimacy breakdowns, risk assessment, and casting guidance before day one." },
  ],
  contact: {
    lead: "Tell me about your production — the story, the scenes, the support you need.",
    lines: ["Based in Burnaby / Vancouver, BC — available everywhere", "Every enquiry is answered personally — your details stay private"],
    labels: {
      name: "Name",
      email: "Email",
      position: "Job Position (optional)",
      production: "Production / Company (optional)",
      projectType: "Project type (optional)",
      union: "Union Registration",
      script: "Attach a script (optional)",
      message: "Message",
    },
    placeholders: {
      name: "Your full name",
      email: "you@production.com",
      position: "e.g. Director, Producer, 1st AD",
      production: "Working title or company",
      projectType: "Select the type of project",
      message: "Tell me about the project, timeline, and the support you're looking for…",
    },
    projectTypes: ["Feature length film", "Short", "Vertical Series", "Play", "Other"],
    unionOptions: ["Union", "Non-Union"],
    requiredFields: {
      name: true,
      email: true,
      position: false,
      production: false,
      projectType: false,
      union: false,
      message: true,
    },
    fontSizes: { lead: 22, lines: 12, label: 12, input: 18 },
    submitLabel: "Send Inquiry",
    sendingLabel: "Sending…",
    successMessage: "Thank you — your enquiry has been sent. Chloe will be in touch soon.",
    validationMessage: "Please complete the highlighted fields.",
  },
  footer: {
    name: "Chloe Donan",
    tagline: "Intimacy Coordinator — Film · TV · Theatre",
    instagramUrl: "https://www.instagram.com/chloemcrae.ic",
    instagramLabel: "@chloemcrae.ic",
  },
  bio: {
    lead: "I've stood on every side of an intimate scene — actor, agent, crew. I know what performers need, because I've needed it too.",
    paragraphs: "Chloe Donan is an intimacy coordinator and script intimacy consultant based in Burnaby, BC, working across film, television, verticals, and theatre. Since 2016 she has worked throughout the commercial, voiceover, film and TV industries — as a talent agent, actor, film crew member, photo double, and special ability performer.\n\nThat decade on both sides of the camera gives her a nuanced understanding of performers' needs and real industry dynamics. Her practice is rooted in a simple conviction: foster supportive work environments and promote healthy professional boundaries — so every artist can do brave work, safely.",
    tags: ["Launchpad-trained · PIP Phases 1–3", "Mental Health First Aid certified", "Trauma-informed practice"],
  },
  resumeCard: {
    title: "Full Résumé",
    text: "A complete list of credits, training, and references is available as a downloadable PDF.",
    buttonLabel: "Download PDF",
  },
  credits: [
    { type: "Feature Film", production: "Venari", byline: "Dir. Taylor Sukert", role: "Intimacy Coordinator" },
    { type: "Vertical Series", production: "Our Side of the World", byline: "Dir. Noah Penner", role: "Intimacy Coordinator" },
    { type: "Vertical Series", production: "Love Under Construction", byline: "Dir. Fei Yu", role: "Intimacy Coordinator" },
    { type: "Vertical Series", production: "Flee to the Ruthless Mafia Boss", byline: "Dir. Simeon Hu", role: "Intimacy Coordinator" },
    { type: "Vertical Series", production: "Love Beyond Sight", byline: "Dir. Danny Farber", role: "Intimacy Coordinator" },
    { type: "Short Film", production: "Too Far Out", byline: "Dir. Angelika Padberg", role: "Intimacy Coordinator" },
    { type: "Short Film", production: "Suck", byline: "Prod. Alyssa Parker", role: "Intimacy Coordinator" },
    { type: "Short Film", production: "The Loophole", byline: "Dir. Annabelle Loi", role: "Intimacy Coordinator" },
    { type: "Short Film", production: "Zero Nine Six", byline: "Dir. Nicholas Kedrock", role: "Intimacy Coordinator" },
    { type: "Short Film", production: "Dial Tone", byline: "Dir. Loughran MacLeod", role: "Intimacy Coordinator" },
    { type: "Theatre", production: "Alligator Hunting", byline: "UBC Players Club", role: "Intimacy Director" },
  ],
  training: [
    { title: "Launchpad Training — Phases 1, 2 & 3", org: "Principal Intimacy Professionals", detail: "Intimacy coordination craft: consent practices, choreography, documentation" },
    { title: "Mental Health First Aid", org: "National Council for Mental Wellbeing", detail: "Certified — recognising and responding to distress on set" },
    { title: "Diploma in Acting for Film, TV & Voiceover", org: "Vancouver Acting School", detail: "Formal performance training across screen and voice work" },
    { title: "Trauma Recovery", org: "Mitch Artman, LCSW, MA", detail: "Trauma-informed approaches for supporting performers" },
    { title: "Building Blocks to Boundaries", org: "Katie Strub, MA, LMHC, EMDR", detail: "Boundary frameworks for healthy professional practice" },
    { title: "Bystander Intervention", org: "Right To Be Training", detail: "5D methodology for safe, respectful workplaces" },
    { title: "Handling Disclosures of Intimate Partner Violence", org: "Constable Sherri Wade", detail: "Responding appropriately when a colleague discloses" },
    { title: "Intro to Gender Diversity", org: "Provincial Health Services Authority", detail: "Inclusive, affirming practice for gender-diverse artists" },
    { title: "Supporting Gender Creative Children & Their Families", org: "Provincial Health Services Authority", detail: "Care-centred support frameworks" },
    { title: "Conflict Management", org: "Great Learning Academy", detail: "De-escalation and resolution on fast-moving sets" },
    { title: "Learning to Destress", org: "Jaret Romano, MA, NCC, LPC", detail: "Identifying and releasing stress from the body" },
  ],
  testimonials: [
    { quote: "Chloe is incredibly thorough in her approach to ensuring that performers feel safe, informed, and empowered in their choices on set. She is a strong conduit of support for performers and production, both on and off set. Her professionalism and dedication to her craft was an immense benefit to our production, and will benefit any production lucky to have her.", name: "Taylor Sukert", role: "Director/Writer — Venari", featured: true },
    { quote: "Chloe made a difficult scene easy, and it became not only the climactic scene in the story, but also the best scene of the film. Her work has my highest recommendation.", name: "Noah Penner", role: "Director/Writer — Our Side of the World", featured: false },
    { quote: "Chloe was thoughtful, professional, and deeply supportive of my vision as a director while always ensuring the cast felt safe and comfortable throughout the process.", name: "Nicholas Kedrock", role: "Director/Writer — Zero Nine Six", featured: false },
    { quote: "Chloe's choreo really made the section stand out in ways I don't think I could have done alone. The actors had the audience gasping in the best and most endearing way possible every night.", name: "Addison Forster", role: "Director — Alligator Hunting", featured: false },
    { quote: "It was really appreciated when you helped to explain movements, for me and for the actors… I found myself in more of a groove with your reminder to speak more slowly and deliberately with cast. Just needed to hear it, applied it, and got some really wicked stuff out of it.", name: "Loughran MacLeod", role: "Director/Writer — Dial Tone", featured: false },
  ],
};

export const ContentContext = createContext(DEFAULT_CONTENT);
export const useContent = () => useContext(ContentContext);
